import throttle from 'raf-throttle'
import {
  template,
  Core,
  EventMap,
  AttributeMap,
  TemplateStringifier,
  Level,
  SupportedVersion
} from '@clappr/core'
import ClapprLevelSelector from 'level-selector'
import {MediaControl} from 'common/plugins/media-control'
import {Share} from 'common/plugins/share'
import {Options} from 'common/types/options'
import {Events} from 'common/types/events'
import styles from './styles.scss'
import {Scrollbar} from './scrollbar'
import pluginHtml from './template'
import PlaybackRatePlugin from 'clappr-playback-rate-plugin'

const labelCallback = (playbackLevel: Level): string =>
  `${playbackLevel.level.height}p`

const levelCompare = (a: Level, b: Level): number =>
  b.level.height - a.level.height

export class SettingsMenu<T extends Options> extends ClapprLevelSelector<T> {
  public get name(): string {
    return 'settingsMenu'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get template(): TemplateStringifier {
    return template(pluginHtml)
  }

  public get attributes(): AttributeMap {
    return {
      class: `${styles.settings} eplayer-skin-shiftable eplayer-skin-fullsize`,
      'data-settings-menu': ''
    }
  }

  public get events(): EventMap {
    return {
      'click [data-menu-close]': 'close',
      'click [data-level]': 'onLevelSelect',
      'click [data-rate]': 'onRateSelect'
    }
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  private get share(): Share<T> | null {
    return this.core.getPlugin('share') as Share<T> | null
  }

  private get playbackRate(): PlaybackRatePlugin<T> | null {
    return this.core.getPlugin('playbackRate') as PlaybackRatePlugin<T> | null
  }

  public get levelsList(): Level[] {
    const {qualityVisible} = this.core.options

    return (
      (qualityVisible &&
        this.shouldRender() &&
        this.levels.sort(levelCompare)) ||
      []
    )
  }

  public constructor(core: Core<T>) {
    super(core)

    const {options} = this.core

    if (!options.levelSelectorConfig) {
      options.levelSelectorConfig = {}
    }

    if (!options.levelSelectorConfig.labelCallback) {
      options.levelSelectorConfig.labelCallback = labelCallback
    }
  }

  private onClickOutside = (ev: MouseEvent): void => {
    if (this.core.$el.hasClass('settings-menu-visible')) {
      const target = ev.target as HTMLDivElement

      if (
        !target.hasAttribute('data-settings-menu') &&
        (target.closest('[data-settings-menu]') ||
          target.closest('[data-menu="menu"]') ||
          target.closest('[data-menu="levels"]') ||
          target.closest('[data-menu="rates"]'))
      ) {
        return
      }

      this.close()
    }
  }

  public bindEvents(): void {
    super.bindEvents()

    document.addEventListener('click', this.onClickOutside)

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_MENU_CLICK,
        this.show
      )
    }

    if (this.share) {
      this.listenTo(this.share, Events.SHARE_OPEN, this.close)
    }

    this.listenTo(this.core, Events.CORE_RESIZE, this.onParentResize)
  }

  public bindPlaybackEvents(): void {
    super.bindPlaybackEvents()

    if (this.core.activePlayback) {
      this.listenTo(
        this.core.activePlayback,
        Events.PLAYBACK_LEVEL_SWITCH_START,
        this.onLevelChanged
      )
    }
  }

  public unBindEvents(): void {
    super.unBindEvents()

    document.removeEventListener('click', this.onClickOutside)

    if (this.mediaControl) {
      this.stopListening(
        this.mediaControl,
        Events.MEDIACONTROL_MENU_CLICK,
        this.show
      )
    }

    if (this.share) {
      this.stopListening(this.share, Events.SHARE_OPEN, this.close)
    }

    this.stopListening(this.core, Events.CORE_RESIZE, this.onParentResize)

    if (this.core.activePlayback) {
      this.stopListening(
        this.core.activePlayback,
        Events.PLAYBACK_LEVEL_SWITCH_START,
        this.onLevelChanged
      )
    }
  }

  private onParentResize = throttle(() => {
    this.scrollbar?.update()
  })

  private scrollbar?: Scrollbar

  protected show(section: string): void {
    const $element = this.$el.find(`[data-menu-section="${section}"]`)

    if ($element.is(':visible')) {
      this.close()
    } else {
      this.mediaControl?.$el
        .find('[data-menu-item-active]')
        .removeAttr('data-menu-item-active')
      this.core.$el.toggleClass('settings-menu-visible', true)
      this.$el.show()

      this.$el.find('[data-menu-section]').hide()
      $element.show()
      this.mediaControl?.$el
        .find(`[data-menu="${section}"]`)
        .attr('data-menu-item-active', '')

      this.scrollbar ??= new Scrollbar(this.$el.find('[data-menu-inner]'))
      this.scrollbar.update()

      if (section === 'rates') {
        this.scrollbar.scrollToBottom()
      }
    }
  }

  protected close(): void {
    this.$el.hide()
    this.$el.find('[data-menu-section]').hide()
    this.core.$el.toggleClass('settings-menu-visible', false)
    this.mediaControl?.$el
      .find('[data-menu-item-active]')
      .removeAttr('data-menu-item-active')
    this.trigger(Events.SETTINGS_MENU_CLOSE)
  }

  public onLevelChanged(): void {
    if (this.core.activePlayback.currentLevel !== this.selectedLevelId) {
      this.selectedLevelId = this.core.activePlayback.currentLevel
      this.highlightCurrentLevel()
    }
  }

  public onRateSelect(event: Event): void {
    const {rate} = (event.target as HTMLElement).dataset

    this.playbackRate?.setSelectedRate(rate as string)

    this.close()
    this.highlightCurrentRate()

    this.trigger(Events.SETTINGS_MENU_RATE_SELECTOR_CLICK, rate)
  }

  public onLevelSelect(event: Event): void {
    const {level = ''} = (event.target as HTMLElement).dataset

    const levelId = parseInt(level, 10)

    if (this.core.activePlayback.currentLevel !== levelId) {
      try {
        // 54. Когда видео закончилось, падает ошибка в консоль при попытке изменить качество видео
        // (hls.js:38 Uncaught TypeError: Cannot set property 'currentLevel' of undefined)
        this.core.activePlayback.currentLevel = levelId
      } catch {}
    }

    this.close()

    this.trigger(Events.SETTINGS_MENU_LEVEL_SELECTOR_CLICK, level)
  }

  public updateCurrentLevel(info: {level: number}): void {
    const levelId = info.level

    if (this.selectedLevelId !== levelId) {
      const level = this.findLevelBy(levelId)

      this.currentLevel = level ? level : null
      this.highlightCurrentLevel()
    }
  }

  public highlightCurrentLevel(): void {
    const $items = this.$el.find('[data-level]')

    if ($items.length) {
      const $active = this.$el.find(`[data-level="${this.selectedLevelId}"]`)

      $items.toggleClass(styles.active, false)
      $active.toggleClass(styles.active, true)
      this.$el.find('[data-level-label]').text($active.text())
    }
  }

  public highlightCurrentRate(): void {
    const $items = this.$el.find('[data-rate]')

    if ($items.length) {
      const $active = this.$el.find(
        `[data-rate="${this.playbackRate?.currentSelectedRate}"]`
      )

      $items.toggleClass(styles.active, false)
      $active.toggleClass(styles.active, true)
      this.$el.find('[data-rate-label]').text($active.text())
    }
  }

  public destroy(): void {
    this.scrollbar?.destroy()
    delete this.scrollbar
    super.destroy()
  }

  public stopListening(
    object?: unknown,
    name?: string,
    callback?: (event: any) => void
  ): this {
    if (!callback) {
      document.removeEventListener('click', this.onClickOutside)
    }

    return super.stopListening(object, name, callback)
  }

  public render(): this {
    const {playbackRate, share} = this.core.options

    if (this.mediaControl) {
      const rates = (playbackRate && this.playbackRate?.playbackRatesList) || []

      this.core.$el
        .toggleClass('settings-menu-with-rates', !!rates.length)
        .toggleClass('settings-menu-with-levels', !!this.levelsList.length)

      if (rates.length || this.levelsList.length) {
        this.$el.html(
          this.template({
            levels: this.levelsList,
            rates,
            share
          })
        )
        this.close()
        this.mediaControl.$el.append(this.el)
        this.highlightCurrentLevel()
        this.highlightCurrentRate()
      }
    }

    return this
  }
}
